import { t } from '@if/core/i18n'
import {
  EApplicantType,
  ECreditApplicationStatus,
  ECreditApplicationStep,
  EDecision,
  EDocumentStatus,
  EDocumentType,
  EFinancingProgram,
  ELoanPurpose,
  EPaymentPlan,
  EProvince,
  EStaticMerchantIds,
  ETasksStatus,
  ETaskType,
  EWorksheetDecision,
} from '@if/core/types'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFraudFlags } from '../api/fraud/useFraud'
import { useGetMerchantById } from '../api/merchant/merchant-api'
import { useGetPreviousApplicationsQuery } from '../api/previous-application/previous-application-api'
import {
  Applicant,
  CreditApplication,
  CreditApplicationTask,
  EAfterNormEffectsEnum,
  NormsMessage,
  PersonalLoanWorksheet,
  PreviousApplication,
} from '../types'
import { MerchantPaymentPlan } from '../types/MerchantPaymentPlan'
import { ParameterizedMessage } from '../types/ParameterizedMessage'
import { PreviousApplicationInputDto } from '../types/PreviousApplicationInputDto'

// Helper function to check if all tasks for a step are completed
function allTasksCompletedForStep(step: ECreditApplicationStep, tasks: CreditApplicationTask[]) {
  return !tasks.some((x) => x.status === ETasksStatus.ToDo && x.requiredBeforeStep === step)
}

// Helper function to get norm messages from codes (translation happens in component)
function getTranslationFromListOfKeys(codes: string[], applicantType: EApplicantType | null): NormsMessage[] {
  return codes.map((code) => ({
    id: code,
    message: code, // Use code as message - will be translated by CreditWarningMessageList
    applicantType,
  }))
}

// Helper function to build parameterized norm message (with translation)
// Used for worksheet messages that have parameters
function buildParameterizedNormMessage(message: ParameterizedMessage): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
  const translatedMessage = (t as any)(`worksheet.norms.${message.id}`) as string

  if (message.parameters.length === 0) {
    return translatedMessage
  }

  let messageSuffix = ' | '
  message.parameters.forEach((p, i) => {
    if (i >= 1) messageSuffix = messageSuffix.concat(', ')

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call
    const suffix = (t as any)(`worksheet.paramNames.${p.name}`, { replace: { val: p.value } }) as string
    messageSuffix = messageSuffix.concat(suffix)
  })

  return translatedMessage.concat(messageSuffix)
}

// Helper function to get norm messages for application
function getNormMessagesForApplication(
  warningCodes?: string[] | null,
  refusalReasonCodes?: string[] | null,
): NormsMessage[] {
  let normsList: NormsMessage[] = []
  if (warningCodes) normsList = normsList.concat(getTranslationFromListOfKeys(warningCodes, null))
  if (refusalReasonCodes) normsList = normsList.concat(getTranslationFromListOfKeys(refusalReasonCodes, null))
  return normsList
}

/**
 * Hook to check if final decision is pending
 * Returns true when waiting for norms decision after hard hit consent has been given
 */
export function useIsFinalDecisionPending() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return (
      creditApplication?.normsCreditDecision === null &&
      creditApplication?.finalCreditDecision?.decision === EDecision.Pending &&
      creditApplication?.status === ECreditApplicationStatus.Active &&
      creditApplication?.consentSoftHit === true &&
      creditApplication?.consentHardHit === true
    )
  }, [
    creditApplication?.normsCreditDecision,
    creditApplication?.finalCreditDecision?.decision,
    creditApplication?.status,
    creditApplication?.consentSoftHit,
    creditApplication?.consentHardHit,
  ])
}

/**
 * Hook to get current credit application specific norms messages
 * Filters out refusal codes that already appear on the applicant or co-applicant level
 */
export function useCurrentCreditApplicationSpecificNorms() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    const applicantCodes = creditApplication?.applicant.creditDecision?.refusalReasonCodes ?? []
    const coApplicantCodes = creditApplication?.coApplicant?.creditDecision?.refusalReasonCodes ?? []
    const applicantPrequalCodes = creditApplication?.applicant.prequalificationDecision?.refusalReasonCodes ?? []
    const coApplicantPrequalCodes = creditApplication?.coApplicant?.prequalificationDecision?.refusalReasonCodes ?? []
    const perApplicantCodes = new Set([
      ...applicantCodes,
      ...coApplicantCodes,
      ...applicantPrequalCodes,
      ...coApplicantPrequalCodes,
    ])

    const filteredRefusalCodes = creditApplication?.finalCreditDecision?.refusalReasonCodes?.filter(
      (code) => !perApplicantCodes.has(code),
    )

    return getNormMessagesForApplication(creditApplication?.finalCreditDecision?.warningCodes, filteredRefusalCodes)
  }, [
    creditApplication?.finalCreditDecision?.warningCodes,
    creditApplication?.finalCreditDecision?.refusalReasonCodes,
    creditApplication?.applicant.creditDecision?.refusalReasonCodes,
    creditApplication?.coApplicant?.creditDecision?.refusalReasonCodes,
    creditApplication?.applicant.prequalificationDecision?.refusalReasonCodes,
    creditApplication?.coApplicant?.prequalificationDecision?.refusalReasonCodes,
  ])
}

/**
 * Hook to check if all tasks required for CVT are completed
 */
export function useAreAllTasksRequiredForCVTCompleted() {
  const { tasks } = useCreditApplicationData()
  return useMemo(() => {
    const tasksWithoutApproveWorksheet = tasks.filter((x) => x.typeId !== ETaskType.ApproveWorksheet)
    return (
      allTasksCompletedForStep(ECreditApplicationStep.CVT, tasksWithoutApproveWorksheet) &&
      allTasksCompletedForStep(ECreditApplicationStep.Financing, tasksWithoutApproveWorksheet) &&
      allTasksCompletedForStep(ECreditApplicationStep.Credit, tasksWithoutApproveWorksheet)
    )
  }, [tasks])
}

/**
 * Hook to get confirm all incomes status
 */
export function useConfirmAllIncomesStatus() {
  const { tasks } = useCreditApplicationData()
  return useMemo(() => {
    return tasks.find((x) => x.typeId === ETaskType.ConfirmAllIncomes)?.status
  }, [tasks])
}

/**
 * Hook to get task list to display (filters out ConfirmAllIncomes)
 */
export function useTaskListToDisplay() {
  const { tasks } = useCreditApplicationData()
  return useMemo(() => {
    return tasks.filter((x) => x.typeId !== ETaskType.ConfirmAllIncomes)
  }, [tasks])
}

/**
 * Hook to check if refinancing
 * @param creditApp - Optional credit application to check. If not provided, uses context.
 */
export function useIsRefinancing(creditApp?: CreditApplication | null) {
  // If creditApp is provided, use it directly (for create flow where context doesn't exist)
  return useMemo(() => {
    return !!creditApp?.refinancing
  }, [creditApp?.refinancing])
}

/**
 * Hook to check if only prequalification is done
 * Matches original Redux selector logic: prequalification completed but no norms decision yet
 * (soft hit done, hard hit not done)
 */
export function useIsOnlyPrequalificationDone() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return (
      creditApplication?.normsCreditDecision === null &&
      creditApplication?.prequalificationDecision !== null &&
      creditApplication?.consentSoftHit === true &&
      creditApplication?.consentHardHit === false
    )
  }, [
    creditApplication?.normsCreditDecision,
    creditApplication?.prequalificationDecision,
    creditApplication?.consentSoftHit,
    creditApplication?.consentHardHit,
  ])
}

/**
 * Hook to get Flinks incomes for applicant
 */
export function useGetFlinksIncomesForApplicant() {
  const { flinksIncomes } = useCreditApplicationData()
  return useMemo(() => {
    const flinksForApplicant = flinksIncomes.filter((x) => x.applicantType === EApplicantType.Applicant)
    return flinksForApplicant.length > 0 ? flinksForApplicant[0] : null
  }, [flinksIncomes])
}

/**
 * Hook to get Flinks incomes for co-applicant
 */
export function useGetFlinksIncomesForCoApplicant() {
  const { flinksIncomes } = useCreditApplicationData()
  return useMemo(() => {
    const flinksForCoApplicant = flinksIncomes.filter((x) => x.applicantType === EApplicantType.CoApplicant)
    return flinksForCoApplicant.length > 0 ? flinksForCoApplicant[0] : null
  }, [flinksIncomes])
}

/**
 * Hook to get fraud analysis result (single object with sub-properties)
 */
export function useGetFraudAnalysis() {
  const { fraudAnalysis } = useCreditApplicationData()
  return fraudAnalysis
}

/**
 * Hook to get IPQS analysis for a specific applicant type
 */
export function useGetIPQSAnalysis(applicantType: EApplicantType) {
  const { fraudAnalysis } = useCreditApplicationData()
  return useMemo(() => {
    return applicantType === EApplicantType.CoApplicant
      ? fraudAnalysis?.coapplicantIPQSAnalysis
      : fraudAnalysis?.applicantIPQSAnalysis
  }, [fraudAnalysis, applicantType])
}

/**
 * Hook to get fraud warning messages based on fraud flags response and analysis results
 */
export function useFraudWarningMessages(
  applicantType: EApplicantType,
  enabled: boolean,
): NormsMessage[] {
  const { i18n } = useTranslation()
  const { creditApplication, fraudAnalysis, fraudFlags } = useCreditApplicationData()
  const isPrequalificationPending = useIsPrequalificationPending()
  const finalDecisionIsPending = useIsFinalDecisionPending()
  const pending = !isPrequalificationPending && !finalDecisionIsPending
  const ipqsAnalysis = useGetIPQSAnalysis(applicantType)
  const skipCoApplicant = applicantType === EApplicantType.CoApplicant && !creditApplication?.coApplicant
  const [fraudFlagsResponse] = useFraudFlags(creditApplication?.financingProgramId || '', creditApplication?.id || '', fraudFlags?.updatedOn || '', pending && !skipCoApplicant)
  return useMemo(() => {
    if (!enabled || skipCoApplicant) return []
    const hideIpqsFraudIq =
      creditApplication?.financingProgramId === EFinancingProgram.IfXpress ||
      creditApplication?.financingProgramId === EFinancingProgram.MechanicalWarranty

    const flags = fraudFlagsResponse?.flags[applicantType] ?? []
    const manualFlags = flags.filter((f) => f.value && !f.isAutomatic)
    const autoFlags = flags.filter((f) => f.value && f.isAutomatic)

    const messages: NormsMessage[] = []

    if (
      !hideIpqsFraudIq &&
      fraudAnalysis?.fraudIQAnalysis?.decision &&
      fraudAnalysis.fraudIQAnalysis.decision !== 'CLEAR'
    ) {
      messages.push({
        id: 'FraudIQNotClearWarning',
        message: t('warningMessages.FraudIQNotClearWarning', 'FraudIQ analysis result is not clear'),
        applicantType
      })
    }
    if (!hideIpqsFraudIq && (ipqsAnalysis?.emailFraudScore ?? 0) > 0) {
      messages.push({
        id: 'IPQSEmailScore',
        message: `${t('fraudFlags.scores.ipqsEmail', 'IPQS Email Score')}: ${ipqsAnalysis?.emailFraudScore}`,
        applicantType
      })
    }
    if (!hideIpqsFraudIq && (ipqsAnalysis?.phoneFraudScore ?? 0) > 0) {
      messages.push({
        id: 'IPQSPhoneScore',
        message: `${t('fraudFlags.scores.ipqsPhone', 'IPQS Phone Score')}: ${ipqsAnalysis?.phoneFraudScore}`,
        applicantType
      })
    }
    if (manualFlags.length > 0) {
      const names = manualFlags
        .map((x) => (i18n.language === 'fr' ? x.displayNameFr : x.displayNameEn).trim())
        .join(' / ')
      messages.push({
        id: 'ManualFlags',
        message: `${t('fraudFlags.manual')} ${t('fraudFlags.flags')}: ${names}`,
        applicantType
      })
    }
    if (autoFlags.length > 0) {
      const names = autoFlags
        .map((x) => (i18n.language === 'fr' ? x.displayNameFr : x.displayNameEn).trim())
        .join(' / ')
      messages.push({
        id: 'AutomaticFlags',
        message: `${t('fraudFlags.automatic')} ${t('fraudFlags.flags')} : ${names}`,
        applicantType
      })
    }

    return messages
  }, [enabled, skipCoApplicant, creditApplication?.financingProgramId, fraudFlagsResponse?.flags, applicantType, fraudAnalysis?.fraudIQAnalysis?.decision, ipqsAnalysis?.emailFraudScore, ipqsAnalysis?.phoneFraudScore, i18n.language])
}

/**
 * Hook to check if prequalification is pending
 * Matches original Redux selector logic: only returns true when waiting for prequalification
 * after soft hit consent has been given (but before hard hit)
 */
export function useIsPrequalificationPending() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return (
      creditApplication?.prequalificationDecision === null &&
      creditApplication?.status === ECreditApplicationStatus.Active &&
      creditApplication?.consentSoftHit === true &&
      creditApplication?.consentHardHit === false
    )
  }, [
    creditApplication?.prequalificationDecision,
    creditApplication?.status,
    creditApplication?.consentSoftHit,
    creditApplication?.consentHardHit,
  ])
}

/**
 * Hook to check if refused with no credit report
 * If prequalification is refused, checks softHitReport (since hard hit won't be run if soft hit is refused)
 * Otherwise, checks hardHitReport
 */
export function useIsRefusedWithNoCreditReport() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return (
      creditApplication?.finalCreditDecision?.decision === EDecision.Refused &&
      (creditApplication?.prequalificationDecision?.decision === EDecision.Refused
        ? !creditApplication?.applicant?.softHitReport
        : !creditApplication?.applicant?.hardHitReport)
    )
  }, [
    creditApplication?.finalCreditDecision?.decision,
    creditApplication?.prequalificationDecision?.decision,
    creditApplication?.applicant?.softHitReport,
    creditApplication?.applicant?.hardHitReport,
  ])
}

/**
 * Hook to check if all tasks required for payout are completed
 */
export function useAreAllTasksRequiredForPayoutCompleted() {
  const { tasks } = useCreditApplicationData()
  return useMemo(() => {
    return (
      allTasksCompletedForStep(ECreditApplicationStep.PayOut, tasks) &&
      allTasksCompletedForStep(ECreditApplicationStep.CVT, tasks) &&
      allTasksCompletedForStep(ECreditApplicationStep.Credit, tasks) &&
      allTasksCompletedForStep(ECreditApplicationStep.Financing, tasks)
    )
  }, [tasks])
}

/**
 * Hook to check if has coapplicant
 */
export function useHasCoapplicant() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return creditApplication?.coApplicant !== null
  }, [creditApplication?.coApplicant])
}

/**
 * Hook to check if has CVT
 */
export function useHasCVT() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return !!creditApplication?.cvtNumber
  }, [creditApplication?.cvtNumber])
}

/**
 * Hook to check if merchant has rate modifier
 */
export function useHasMerchantRateModifier() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return (
      creditApplication?.normsCreditDecision &&
      creditApplication.normsCreditDecision.afterNormEffects.some(
        (x) => x === EAfterNormEffectsEnum.MerchantRateModifier,
      )
    )
  }, [creditApplication?.normsCreditDecision])
}

/**
 * Hook to check if credit application was created with a promotion
 */
export function useCreatedWithPromotion() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return (
      creditApplication?.merchantPaymentPlanId != null &&
      creditApplication.merchantPaymentPlanId !== EPaymentPlan.RegularDailyInterests
    )
  }, [creditApplication?.merchantPaymentPlanId])
}

/**
 * Hook to check if personal loan worksheet exists
 */
export function useIsPersonalLoanWorksheetCreated() {
  const { worksheet } = useCreditApplicationData()
  return useMemo(() => {
    return worksheet !== null && worksheet !== undefined
  }, [worksheet])
}

/**
 * Hook to check if personal loan worksheet has a confirmed promotion
 * Returns true if worksheet exists and paymentPlanId is not RegularDailyInterests
 */
export function useHasConfirmedPromotion() {
  const { worksheet } = useCreditApplicationData()
  return useMemo(() => {
    if (!worksheet) return false
    return 'paymentPlanId' in worksheet && worksheet.paymentPlanId !== (EPaymentPlan.RegularDailyInterests as string)
  }, [worksheet])
}

/**
 * Hook to check if all tasks required for credit are completed
 */
export function useAreAllTasksRequiredForCreditCompleted() {
  const { tasks } = useCreditApplicationData()
  return useMemo(() => {
    return allTasksCompletedForStep(ECreditApplicationStep.Credit, tasks)
  }, [tasks])
}

// Helper function to get norm messages for an applicant
function getNormMessagesForApplicant(
  applicant: Applicant | null | undefined,
  applicantType: EApplicantType,
): NormsMessage[] {
  let normsList: NormsMessage[] = []
  if (applicant?.creditDecision) {
    const infoCodes =
      applicant.creditDecision.infoCodes?.map((code) => ({
        id: code,
        message: code,
        applicantType,
      })) ?? []
    const warningCodes =
      applicant.creditDecision.warningCodes?.map((code) => ({
        id: code,
        message: code,
        applicantType,
      })) ?? []
    const refusalCodes =
      applicant.creditDecision.refusalReasonCodes?.map((code) => ({
        id: code,
        message: code,
        applicantType,
      })) ?? []
    normsList = normsList.concat(infoCodes, warningCodes, refusalCodes)
  }
  if (applicant?.prequalificationDecision) {
    const prequalWarningCodes =
      applicant.prequalificationDecision.warningCodes?.map((code) => ({
        id: code,
        message: code,
        applicantType,
      })) ?? []
    const prequalRefusalCodes =
      applicant.prequalificationDecision.refusalReasonCodes?.map((code) => ({
        id: code,
        message: code,
        applicantType,
      })) ?? []
    normsList = normsList.concat(prequalWarningCodes, prequalRefusalCodes)
  }
  return normsList
}

/**
 * Hook to get norm messages for the applicant
 */
export function useCurrentCreditApplicationNormsApplicant() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return getNormMessagesForApplicant(creditApplication?.applicant, EApplicantType.Applicant)
  }, [creditApplication?.applicant])
}

/**
 * Hook to get norm messages for the co-applicant
 */
export function useCurrentCreditApplicationNormsCoapplicant() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return getNormMessagesForApplicant(creditApplication?.coApplicant, EApplicantType.CoApplicant)
  }, [creditApplication?.coApplicant])
}

/**
 * Hook to get previous applicant applications (filtered to exclude current)
 * Fetches from backend using applicant data from the credit application context.
 */
export function useGetPreviousApplicantApplications(): PreviousApplication[] {
  const { creditApplication } = useCreditApplicationData()

  const filter: PreviousApplicationInputDto | null = useMemo(() => {
    if (!creditApplication?.applicant) return null
    const applicant = creditApplication.applicant
    return {
      Sin: applicant.sin ?? '',
      FirstName: applicant.firstName,
      LastName: applicant.lastName,
      DateOfBirth: applicant.birthDate,
      EquifaxUniqueId: applicant.hardHitReport?.creditReportUniqueNumber ?? '',
      FinancingProgramId: creditApplication.financingProgramId,
    }
  }, [creditApplication?.applicant, creditApplication?.financingProgramId])

  const [previousApplications] = useGetPreviousApplicationsQuery(filter)

  return useMemo(() => {
    if (!creditApplication?.id) return []
    return previousApplications.filter((app) => app.creditApplicationId !== creditApplication.id)
  }, [previousApplications, creditApplication?.id])
}

/**
 * Hook to get previous co-applicant applications (filtered to exclude current)
 * Fetches from backend using co-applicant data from the credit application context.
 */
export function useGetPreviousCoApplicantApplications(): PreviousApplication[] {
  const { creditApplication } = useCreditApplicationData()

  const filter: PreviousApplicationInputDto | null = useMemo(() => {
    if (!creditApplication?.coApplicant) return null
    const coApplicant = creditApplication.coApplicant
    return {
      Sin: coApplicant.sin ?? '',
      FirstName: coApplicant.firstName,
      LastName: coApplicant.lastName,
      DateOfBirth: coApplicant.birthDate,
      EquifaxUniqueId: coApplicant.hardHitReport?.creditReportUniqueNumber ?? '',
      FinancingProgramId: creditApplication.financingProgramId,
    }
  }, [creditApplication?.coApplicant, creditApplication?.financingProgramId])

  const [previousApplications] = useGetPreviousApplicationsQuery(filter)

  return useMemo(() => {
    if (!creditApplication?.id) return []
    return previousApplications.filter((app) => app.creditApplicationId !== creditApplication.id)
  }, [previousApplications, creditApplication?.id])
}

/**
 * Hook to get the apply online link based on financing program and merchant
 */
export function useGetApplyOnlineLink() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    if (!creditApplication) return null

    let baseUrl: string | null = null
    switch (creditApplication.financingProgramId) {
      case EFinancingProgram.Personal:
        if (
          creditApplication.loanPurposeId === ELoanPurpose.Medical ||
          creditApplication.loanPurposeId === ELoanPurpose.Aesthetics
        ) {
          baseUrl = import.meta.env.VITE_APPLY_MEDICARD_URL
        } else {
          baseUrl = import.meta.env.VITE_APPLY_PERSONAL_LOAN_URL
        }
        break

      case EFinancingProgram.B2c:
        if (creditApplication.merchantId === EStaticMerchantIds.CIRCLEK_B2C_MERCHANT_ID) {
          baseUrl = import.meta.env.VITE_APPLY_CIRCLEK_URL
        } else if (creditApplication.merchantId === EStaticMerchantIds.NEO_B2C_MERCHANT_ID) {
          baseUrl = import.meta.env.VITE_APPLY_NEO_URL
        } else {
          baseUrl = import.meta.env.VITE_APPLY_B2C_URL
        }
        break

      case EFinancingProgram.MechanicalWarranty:
        baseUrl = `${import.meta.env.VITE_APPLY_MECH_WARRANTY_URL}`
        break

      default:
        baseUrl = null
        break
    }

    if (baseUrl && creditApplication) {
      return `${baseUrl}/creditApplication/${creditApplication.id}`
    }
    return null
  }, [creditApplication])
}

/**
 * Hook to get progression status
 */
export function useProgressionStatus() {
  const { progressionStatus } = useCreditApplicationData()
  return progressionStatus
}

/**
 * Hook to get the set of completed credit application steps
 * Used to disable completed steps in Required For dropdowns
 */
export function useCompletedSteps() {
  const { documents, creditApplication, worksheet } = useCreditApplicationData()
  const confirmAllIncomesStatus = useConfirmAllIncomesStatus()

  return useMemo(() => {
    const completed = new Set<ECreditApplicationStep>()

    if (creditApplication?.finalCreditDecision?.decision === EDecision.Approved)
      completed.add(ECreditApplicationStep.Credit)

    if (
      worksheet?.finalDecision?.decision === EWorksheetDecision.Approved &&
      confirmAllIncomesStatus === ETasksStatus.Completed
    ) {
      completed.add(ECreditApplicationStep.Financing)
    }

    if (documents.some((d) => d.typeId === EDocumentType.SignedCVT && d.status === EDocumentStatus.Approved))
      completed.add(ECreditApplicationStep.CVT)

    if (creditApplication?.loanId) completed.add(ECreditApplicationStep.PayOut)

    return completed
  }, [creditApplication, worksheet, confirmAllIncomesStatus, documents])
}

/**
 * Hook to check if applicant's Law 134 (expenses) is completed
 */
export function useGetApplicantLaw134IsCompleted() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return creditApplication?.applicant?.expenses?.isCompleted
  }, [creditApplication?.applicant?.expenses?.isCompleted])
}

/**
 * Hook to check if co-applicant's Law 134 (expenses) is completed
 */
export function useGetCoApplicantLaw134IsCompleted() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return creditApplication?.coApplicant?.expenses?.isCompleted ?? true
  }, [creditApplication?.coApplicant?.expenses?.isCompleted])
}

/**
 * Hook to check if applicant is from Quebec
 */
export function useGetApplicantIsFromQc() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return creditApplication?.applicant?.currentAddress?.stateIso === EProvince.Quebec
  }, [creditApplication?.applicant?.currentAddress?.stateIso])
}

/**
 * Hook to check if co-applicant is from Quebec
 */
export function useGetCoApplicantIsFromQc() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return creditApplication?.coApplicant?.currentAddress?.stateIso === EProvince.Quebec
  }, [creditApplication?.coApplicant?.currentAddress?.stateIso])
}

/**
 * Hook to check if a "Verify Fraud Alert" task is pending (ToDo)
 */
export function useHasPendingFraudAlert() {
  const { tasks } = useCreditApplicationData()
  return useMemo(() => {
    return tasks.some((x) => x.typeId === ETaskType.VerifyFraudAlert && x.status === ETasksStatus.ToDo)
  }, [tasks])
}

/**
 * Hook to check if can confirm all incomes
 */
export function useGetCanConfirmAllIncomes(canOverrideRefusedDecision: boolean) {
  const { creditApplication, tasks } = useCreditApplicationData()
  return useMemo(() => {
    return (
      creditApplication?.normsCreditDecision !== null &&
      (creditApplication?.normsCreditDecision?.decision !== EDecision.Refused || canOverrideRefusedDecision) &&
      !tasks.some(
        (x) =>
          ((x.typeId === ETaskType.ConfirmJobIncome ||
            x.typeId === ETaskType.ConfirmOtherIncome ||
            x.typeId === ETaskType.VerifyBankruptcyHistory) &&
            x.status === ETasksStatus.ToDo) ||
          (x.typeId === ETaskType.ConfirmAllIncomes && x.status === ETasksStatus.Completed) ||
          (x.typeId === ETaskType.VerifyFraudAlert && x.status === ETasksStatus.ToDo),
      )
    )
  }, [creditApplication?.normsCreditDecision, canOverrideRefusedDecision, tasks])
}

/**
 * Hook to check if Credit RSLA hard hit is required
 */
export function useIsCreditRslaHardHitRequired() {
  const { creditApplication } = useCreditApplicationData()
  return useMemo(() => {
    return (
      creditApplication?.prequalificationDecision?.decision === EDecision.Approved &&
      creditApplication?.prequalificationDecision?.isHardHitRequired
    )
  }, [creditApplication?.prequalificationDecision])
}

/**
 * Hook to get worksheet norm warning messages from all worksheet types
 */
export function useGetWorksheetNormWarningMessages(): NormsMessage[] {
  const { worksheet } = useCreditApplicationData()

  return useMemo(() => {
    if (!worksheet) return []

    const worksheetNormMessages: NormsMessage[] = []

    // Get warning and refusal messages from the worksheet's norms decision
    if (worksheet.normsDecision) {
      const warningsAndRefusals = [
        ...(worksheet.normsDecision.warningMessages ?? []),
        ...(worksheet.normsDecision.refusalMessages ?? []),
      ]

      warningsAndRefusals.forEach((msg) => {
        const builtMessage = buildParameterizedNormMessage(msg)
        worksheetNormMessages.push({
          id: msg.id,
          message: builtMessage,
          applicantType: null,
        })
      })
    }

    return worksheetNormMessages
  }, [worksheet])
}

/**
 * Hook to check if credit application can be locked
 * Requires:
 * - Final credit decision is Approved
 * - Worksheet final decision is Approved
 * - ConfirmAllIncomes task is Completed
 * - Signed CVT document is Approved
 * - If applicant is from Quebec, Law 134 must be completed
 * - Credit application status is NOT Completed
 */
export function useCanLockCreditApplication(): boolean {
  const { creditApplication, worksheet, tasks, documents } = useCreditApplicationData()
  return useMemo(() => {
    if (!creditApplication || !worksheet) return false

    const isCreditApproved = creditApplication.finalCreditDecision?.decision === EDecision.Approved
    const isWorksheetApproved = worksheet.finalDecision?.decision === EWorksheetDecision.Approved
    const hasConfirmAllIncomesCompleted = tasks.some(
      (x) => x.typeId === ETaskType.ConfirmAllIncomes && x.status === ETasksStatus.Completed,
    )
    const hasSignedCvt = documents.some(
      (x) => x.typeId === EDocumentType.SignedCVT && x.status === EDocumentStatus.Approved,
    )
    const isLaw134CompletedIfQc =
      creditApplication.applicant?.currentAddress?.stateIso === EProvince.Quebec
        ? creditApplication.applicant?.expenses?.isCompleted === true
        : true
    const isNotCompleted = creditApplication.status !== ECreditApplicationStatus.Completed

    return (
      isCreditApproved &&
      isWorksheetApproved &&
      hasConfirmAllIncomesCompleted &&
      hasSignedCvt &&
      isLaw134CompletedIfQc &&
      isNotCompleted
    )
  }, [creditApplication, worksheet, tasks, documents])
}

/**
 * Hook to check if the ConfirmJobIncome task is completed for a given applicant.
 * Used to lock the Flinks bank account request flow once income has been confirmed.
 */
export function useIsConfirmJobIncomeCompleted(applicantType: EApplicantType): boolean {
  const { tasks } = useCreditApplicationData()
  return useMemo(
    () =>
      tasks.some(
        (task) =>
          task.typeId === ETaskType.ConfirmJobIncome &&
          task.applicantType === applicantType &&
          task.status === ETasksStatus.Completed,
      ),
    [tasks, applicantType],
  )
}

/**
 * Hook to check if able to print CVT
 * Requires:
 * - Final credit decision is Approved
 * - Worksheet final decision is Approved
 * - If applicant is from Quebec, Law 134 must be completed
 * - If co-applicant is from Quebec, Law 134 must be completed
 */
export function useIsAbleToPrintCvt(): boolean {
  const { creditApplication, worksheet } = useCreditApplicationData()
  const applicantLaw134IsCompleted = useGetApplicantLaw134IsCompleted()
  const coApplicantLaw134IsCompleted = useGetCoApplicantLaw134IsCompleted()
  const applicantIsFromQc = useGetApplicantIsFromQc()
  const coApplicantIsFromQc = useGetCoApplicantIsFromQc()

  return useMemo(() => {
    if (!creditApplication || !worksheet) return false

    const isCreditApproved = creditApplication.finalCreditDecision?.decision === EDecision.Approved
    const isWorksheetApproved = worksheet.finalDecision?.decision === EWorksheetDecision.Approved
    const applicantLaw134Ok = !applicantIsFromQc || (applicantLaw134IsCompleted ?? false)
    const coApplicantLaw134Ok = !coApplicantIsFromQc || (coApplicantLaw134IsCompleted ?? false)

    return isCreditApproved && isWorksheetApproved && applicantLaw134Ok && coApplicantLaw134Ok
  }, [
    creditApplication,
    worksheet,
    applicantLaw134IsCompleted,
    coApplicantLaw134IsCompleted,
    applicantIsFromQc,
    coApplicantIsFromQc,
  ])
}

/**
 * Hook to get merchant payment plan from credit application and merchant
 *
 * @param creditApplication - The credit application (required when used outside context)
 * @param personalLoanWorksheet - Optional personal loan worksheet to get paymentPlanId from
 * @returns The merchant payment plan or null if not found
 */
export function useMerchantPaymentPlan(
  creditApplication: CreditApplication | null | undefined,
  personalLoanWorksheet?: PersonalLoanWorksheet | null,
): MerchantPaymentPlan | null {
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')

  return useMemo(() => {
    const paymentPlanId = personalLoanWorksheet?.paymentPlanId ?? creditApplication?.merchantPaymentPlanId

    if (paymentPlanId && merchant?.paymentPlans && merchant.paymentPlans.length > 0) {
      return merchant.paymentPlans.find((p) => p.id === paymentPlanId) ?? null
    }
    return null
  }, [personalLoanWorksheet?.paymentPlanId, creditApplication?.merchantPaymentPlanId, merchant?.paymentPlans])
}

/**
 * Hook to get the most recent electronic signature based on createdOn date
 * Returns the electronic signature with the latest createdOn timestamp, or undefined if none exist
 */
export function useMostRecentElectronicSignature() {
  const { electronicSignatures } = useCreditApplicationData()

  return useMemo(() => {
    if (electronicSignatures.length === 0) return null

    return electronicSignatures.reduce((mostRecent, current) => {
      return new Date(current.createdOn) > new Date(mostRecent.createdOn) ? current : mostRecent
    })
  }, [electronicSignatures])
}
